import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Eye, Download, Search } from "lucide-react";
import NavBar from "@/library/components/NavBar";
import Footer from "@/library/components/Footer";
import Breadcrumbs from "@/library/components/Breadcrumbs";
import PdfViewerModal from "@/library/components/PdfViewerModal";
import { api, API } from "@/library/lib/api";

const FilesView = () => {
  const { indicatorId } = useParams();
  const [files, setFiles] = useState([]);
  const [search, setSearch] = useState("");
  const [openFile, setOpenFile] = useState(null);
  const [ctx, setCtx] = useState({ indicator: null, domain: null, level: null });

  useEffect(() => {
    api.get(`/indicators?domain_id=`).then(() => {});
    api.get(`/files?indicator_id=${indicatorId}`).then((r) => setFiles(r.data));
    // load context
    api.get("/indicators").then((r) => {
      const ind = r.data.find((i) => i.id === indicatorId);
      if (ind) {
        api.get("/domains").then((dr) => {
          const dom = dr.data.find((d) => d.id === ind.domain_id);
          api.get("/levels").then((lr) => {
            const lvl = dom ? lr.data.find((l) => l.id === dom.level_id) : null;
            setCtx({ indicator: ind, domain: dom, level: lvl });
          });
        });
      }
    });
  }, [indicatorId]);

  const filtered = files.filter((f) => !search || f.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="files-page" className="min-h-screen flex flex-col">
      <NavBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex-1 w-full">
        <Breadcrumbs items={[
          { label: "الرئيسية", to: "/" },
          { label: "المراحل", to: "/library/levels" },
          { label: ctx.level?.name || "...", to: ctx.level ? `/library/levels/${ctx.level.id}` : null },
          { label: ctx.domain?.name || "...", to: ctx.domain ? `/library/domains/${ctx.domain.id}` : null },
          { label: ctx.indicator?.name || "..." }
        ]} />
        <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-gold-dark font-bold text-xs tracking-widest uppercase mb-2">المؤشر</div>
            <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-brand-dark">{ctx.indicator?.name || "الملفات"}</h1>
            <div className="gold-line mt-3"></div>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="file-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="بحث عن ملف..."
              className="w-full pe-10 ps-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-brand focus:ring-2 focus:ring-brand/15 outline-none text-sm"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20 premium-card">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <div className="text-slate-500 font-medium">لا توجد ملفات في هذا المؤشر</div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((f, i) => (
              <motion.div
                key={f.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                data-testid={`file-card-${f.id}`}
                className="premium-card overflow-hidden group flex flex-col"
              >
                <div className="aspect-[3/4] bg-gradient-to-br from-slate-100 to-slate-50 relative overflow-hidden">
                  {f.thumb_path ? (
                    <img
                      src={`${API}/files/${f.id}/thumb`}
                      alt={f.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <FileText className="w-20 h-20 text-brand/30" />
                    </div>
                  )}
                  <div className="absolute top-3 start-3 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-md tracking-wider">PDF</div>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="font-heading font-bold text-brand-dark line-clamp-2 mb-2 min-h-[3rem]">{f.title}</h3>
                  {f.description && <p className="text-xs text-slate-500 line-clamp-2 mb-3">{f.description}</p>}
                  <div className="mt-auto flex gap-2 pt-3 border-t border-slate-100">
                    <button
                      onClick={() => setOpenFile(f.id)}
                      data-testid={`view-btn-${f.id}`}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-brand text-white py-2 rounded-lg text-sm font-bold hover:bg-brand-light transition-colors"
                    >
                      <Eye className="w-4 h-4" /> عرض
                    </button>
                    <a
                      href={`${API}/files/${f.id}/download`}
                      download={f.original_filename}
                      data-testid={`download-btn-${f.id}`}
                      className="flex items-center justify-center gap-1.5 px-3 bg-gold/10 text-gold-dark hover:bg-gold hover:text-white py-2 rounded-lg text-sm font-bold transition-colors"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
      <PdfViewerModal fileId={openFile} onClose={() => setOpenFile(null)} />
      <Footer />
    </div>
  );
};

export default FilesView;
